import { toast, ToastOptions } from 'react-toastify';
import { styled } from '@mui/material';
import WarningOutlineIcon from '@mui/icons-material/Warning';
import { CloseOutlined, Info } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { CloseButtonProps } from 'react-toastify/dist/components';

const StyledNotificationButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'color',
})(({ color }) => ({
    border: 0,
    padding: 0,
    cursor: 'pointer',
    background: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: color,
}));

export interface IUseNotificationHook {
    success: (
        message: string | React.ReactNode,
        options?: ToastOptions<unknown> | undefined,
        title?: string,
    ) => unknown;
    error: (
        message: string | React.ReactNode,
        options?: ToastOptions<unknown> | undefined,
        title?: string,
    ) => unknown;
    warning: (
        message: string | React.ReactNode,
        options?: ToastOptions<unknown> | undefined,
        title?: string,
    ) => unknown;
    info: (
        message: string | React.ReactNode,
        options?: ToastOptions<unknown> | undefined,
        title?: string,
    ) => unknown;
    // warning: (message: string) => unknown
    // info: (message: string) => unknown
}

const useNotification = (): IUseNotificationHook => {
    const colors = {
        successBackground: '#EEFFC5',
        successIcon: '#A4CE3C',
        errorIcon: '#FD675F',
        warningIcon: '#EE843F',
        infoIcon: '#2A458A',
        errorBackground: '#FFD2CF',
        warningBackground: '#FFEED5',
        infoBackground: '#EAF2FF',
    };

    const toastStyle: React.CSSProperties = {
        borderRadius: '16px',
        padding: '12px 16px',
        alignItems: 'center',
        color: '#494A50',
        fontSize: 14,
        fontWeight: 500,
    };

    const closeButton = ({ closeToast }: CloseButtonProps) => (
        <StyledNotificationButton color={'#494A50'} onClick={closeToast}>
            <CloseOutlined sx={{ width: '20px', height: '20px' }} />
        </StyledNotificationButton>
    );

    const NotificationContent = ({
        title,
        message,
    }: {
        title: string;
        message: string | React.ReactNode;
    }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</div>
            <div style={{ fontWeight: 500 }}>{message}</div>
        </div>
    );

    const success = (
        message: string | React.ReactNode,
        options?: ToastOptions<unknown> | undefined,
        title?: string,
    ) => {
        const finalTitle = title ?? 'Éxito';
        toast.success(<NotificationContent title={finalTitle} message={message} />, {
            ...(options && options),
            icon: <CheckCircleIcon sx={{ color: colors.successIcon }} />,
            style: {
                backgroundColor: colors.successBackground,
                ...toastStyle,
            },
            bodyStyle: { gap: 10 },
            position: 'top-center',
            closeButton,
        });
    };

    const error = (
        message: string | React.ReactNode,
        options?: ToastOptions<unknown> | undefined,
        title?: string,
    ) => {
        const finalTitle = title ?? 'Error';
        toast.error(<NotificationContent title={finalTitle} message={message} />, {
            ...(options && options),
            icon: <ErrorIcon sx={{ color: colors.errorIcon }} />,
            style: {
                backgroundColor: colors.errorBackground,
                ...toastStyle,
            },
            bodyStyle: { gap: 10 },
            position: 'top-center',
            closeButton,
        });
    };

    const warning = (
        message: string | React.ReactNode,
        options?: ToastOptions<unknown> | undefined,
        title?: string,
    ) => {
        const finalTitle = title ?? 'Alerta';
        toast.warning(<NotificationContent title={finalTitle} message={message} />, {
            ...(options && options),
            icon: <WarningOutlineIcon sx={{ color: colors.warningIcon }} />,
            style: {
                backgroundColor: colors.warningBackground,
                ...toastStyle,
            },
            bodyStyle: { gap: 10 },
            position: 'top-center',
            closeButton,
        });
    };
    const info = (
        message: string | React.ReactNode,
        options?: ToastOptions<unknown> | undefined,
        title?: string,
    ) => {
        const finalTitle = title ?? 'Información';
        toast.info(<NotificationContent title={finalTitle} message={message} />, {
            ...(options && options),
            icon: <Info sx={{ color: colors.infoIcon }} />,
            style: {
                backgroundColor: colors.infoBackground,
                ...toastStyle,
            },
            bodyStyle: { gap: 10 },
            position: 'top-center',
            closeButton,
        });
    };

    return {
        success,
        error,
        warning,
        info,
    };
};

export default useNotification;
